/**
 * 主题：把 palette.js 里采样自设计稿的颜色写成 CSS 自定义属性，并支持日/夜切换。
 *
 * 日间色全部来自设计稿（见 palette.js，直接改那份表）。
 * 夜间色设计稿没有，在这里手工给一套覆盖值 —— 只覆盖背景/文字/面板，
 * **区域色不动**：那 12 种颜色是玩法的识别系统，换掉会让熟悉的棋盘变陌生。
 * 夜间下靠棋盘整体降亮度来压对比，见 game.css 的 [data-theme='dark'] .sd-board。
 */

import { THEME } from './palette.js';

/** 半透明面板在日间是白色叠加，夜间要换成白色低透明度否则会糊成一片。 */
const LIGHT_EXTRA = {
    panel: 'rgba(255, 255, 255, 0.55)',
    panelSoft: 'rgba(255, 255, 255, 0.5)',
    panelStrong: 'rgba(255, 255, 255, 0.72)',
    panelInput: 'rgba(255, 255, 255, 0.85)',
    popupEdge: 'rgba(255, 255, 255, 0.7)',
    onAccent: '#FFFFFF',
};

/** 夜间覆盖值。键名与 THEME/LIGHT_EXTRA 对齐，缺的沿用日间。 */
const DARK_OVERRIDE = {
    pageTop: '#242030',
    pageBottom: '#1A1722',
    boardPanel: '#302B3C',
    boardGap: '#282336',
    ruleIdle: '#3B3550',
    ruleActive: '#C2604E',
    progressTrack: '#4B3A2E',
    textBrown: '#F1E7DC',
    redCross: '#C33C25',
    btnPurple: '#6A33C9',

    panel: 'rgba(255, 255, 255, 0.09)',
    panelSoft: 'rgba(255, 255, 255, 0.07)',
    panelStrong: 'rgba(255, 255, 255, 0.16)',
    panelInput: 'rgba(0, 0, 0, 0.28)',
    popupEdge: 'rgba(255, 255, 255, 0.12)',
};

/** 驼峰 → 短横线：pageTop → page-top */
function kebab(name) {
    return name.replace(/[A-Z]/g, m => '-' + m.toLowerCase());
}

/** @typedef {'light' | 'dark'} ThemeMode */

/** 系统偏好。 */
export function systemMode() {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * 应用主题。
 * @param {ThemeMode} mode
 * @param {HTMLElement} [target]
 */
export function applyTheme(mode = 'light', target = document.documentElement) {
    const vars = { ...THEME, ...LIGHT_EXTRA };
    if (mode === 'dark') Object.assign(vars, DARK_OVERRIDE);

    for (const [key, value] of Object.entries(vars)) {
        target.style.setProperty(`--sd-${kebab(key)}`, value);
    }
    target.dataset.theme = mode;
    // 让浏览器的表单控件、滚动条也跟着切
    target.style.colorScheme = mode;
}
